/**
 * Phase 16 — Admin Partner Opportunity Workflow page.
 *
 * Route: /admin/partners/opportunities (admin only)
 *
 * Surfaces:
 *   1. Stat strip — total / open / terminal / estimated_open_value
 *   2. Filter bar — partner / region / category / workflow_status /
 *                   internal_owner / include_terminal
 *   3. Workflow Action Queue — one row per (booking, category).
 *      Inline controls: workflow_status select, internal_owner input,
 *      internal_note textarea, save. Audit log + notes preview.
 *
 * SAFETY (pinned):
 *   • Admin-only. No emails / SMS / invites / work orders / quotes /
 *     ServiceM8 / commission / pricing / white-label changes.
 *   • Status / owner / note changes are admin-only and audit-logged.
 *   • Calm wording — no medical claims, no guaranteed revenue.
 *   • BioCleanse is supplementary only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  Workflow, Filter, ExternalLink, ChevronDown, ChevronRight,
  History, MessageSquare, User,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const WINDOWS = [
  { id: "all",        label: "All time" },
  { id: "this_month", label: "This month" },
  { id: "last_30d",   label: "Last 30d" },
  { id: "last_7d",    label: "Last 7d" },
];

const STATUS_TONES = {
  new:                  "bg-cyan-100 text-cyan-800",
  reviewed:             "bg-violet-100 text-violet-800",
  partner_to_contact:   "bg-amber-100 text-amber-800",
  partner_contacted:    "bg-amber-100 text-amber-900",
  customer_to_contact:  "bg-orange-100 text-orange-800",
  customer_contacted:   "bg-orange-100 text-orange-900",
  quote_requested:      "bg-blue-100 text-blue-800",
  quoted:               "bg-blue-100 text-blue-900",
  won:                  "bg-emerald-100 text-emerald-800",
  lost:                 "bg-rose-100 text-rose-800",
  not_applicable:       "bg-slate-100 text-slate-500",
  closed:               "bg-slate-200 text-slate-700",
};

const fmtNum = (n) => Number(n || 0).toLocaleString();

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (!v) return "$0";
  return `$${Math.round(v).toLocaleString()}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); }
  catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); }
  catch { return iso; }
};

const prettyStatus = (s) => (s || "").replaceAll("_", " ");


// ── Window selector ─────────────────────────────────────────────────────
function WindowSelector({ window, onChange }) {
  return (
    <div
      className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold"
      data-testid="ph16-window-selector"
    >
      {WINDOWS.map((w) => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          data-testid={`ph16-window-${w.id}`}
          className={`px-3 py-1 rounded-full transition ${
            window === w.id
              ? "bg-amber-600 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-800"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}


// ── Stat strip ──────────────────────────────────────────────────────────
function StatStrip({ summary }) {
  return (
    <div
      data-testid="ph16-stat-strip"
      className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3"
    >
      <div
        data-testid="ph16-stat-total"
        className="rounded-lg p-3 bg-white border border-slate-200"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          Total opportunities
        </div>
        <div className="mt-1 text-lg font-bold">
          {fmtNum(summary?.total)}
        </div>
      </div>
      <div
        data-testid="ph16-stat-open"
        className="rounded-lg p-3 bg-amber-50 border border-amber-100"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
          Open
        </div>
        <div className="mt-1 text-lg font-bold text-amber-900">
          {fmtNum(summary?.open_count)}
        </div>
      </div>
      <div
        data-testid="ph16-stat-terminal"
        className="rounded-lg p-3 bg-slate-50 border border-slate-200"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
          Closed / lost / N/A
        </div>
        <div className="mt-1 text-lg font-bold text-slate-700">
          {fmtNum(summary?.terminal_count)}
        </div>
      </div>
      <div
        data-testid="ph16-stat-open-value"
        className="rounded-lg p-3 bg-emerald-50 border border-emerald-100"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
          Estimated open value
        </div>
        <div className="mt-1 text-lg font-bold text-emerald-900">
          {fmtMoney(summary?.estimated_open_value)}
        </div>
        <div className="text-[9px] text-emerald-700 italic">
          indicative only — not a quote
        </div>
      </div>
    </div>
  );
}


// ── Filter bar ──────────────────────────────────────────────────────────
function FilterBar({
  partners, regions, categories, statuses, owners,
  partnerId, region, category, workflowStatus, internalOwner, includeTerminal,
  onPartnerId, onRegion, onCategory, onStatus, onOwner, onIncludeTerminal,
  onClear,
}) {
  const hasFilters =
    partnerId || region || category || workflowStatus ||
    internalOwner || includeTerminal;
  return (
    <section
      data-testid="ph16-filter-bar"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          <Filter className="w-3.5 h-3.5 text-amber-700" /> Filters
        </div>
        {hasFilters && (
          <button
            data-testid="ph16-filters-clear"
            onClick={onClear}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 text-xs">
        <select
          data-testid="ph16-filter-partner"
          value={partnerId || ""}
          onChange={(e) => onPartnerId(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All partners</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <select
          data-testid="ph16-filter-region"
          value={region || ""}
          onChange={(e) => onRegion(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All regions</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          data-testid="ph16-filter-category"
          value={category || ""}
          onChange={(e) => onCategory(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <select
          data-testid="ph16-filter-status"
          value={workflowStatus || ""}
          onChange={(e) => onStatus(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{prettyStatus(s)}</option>
          ))}
        </select>
        <select
          data-testid="ph16-filter-owner"
          value={internalOwner || ""}
          onChange={(e) => onOwner(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All owners</option>
          <option value="__unassigned__">— unassigned —</option>
          {owners.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <label
          data-testid="ph16-filter-include-terminal"
          className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1.5 border border-slate-200 rounded-md bg-white cursor-pointer"
        >
          <input
            type="checkbox"
            checked={!!includeTerminal}
            onChange={(e) => onIncludeTerminal(e.target.checked)}
          />
          Include closed/lost/N/A
        </label>
      </div>
    </section>
  );
}


// ── Inline edit panel (expanded row) ────────────────────────────────────
function InlineEdit({ row, busy, statuses, onSave, onCancel }) {
  const [status, setStatus] = useState(row.workflow_status);
  const [owner, setOwner] = useState(row.internal_owner || "");
  const [note, setNote] = useState("");

  const dirty =
    status !== row.workflow_status ||
    owner !== (row.internal_owner || "") ||
    note.trim().length > 0;

  return (
    <div
      data-testid={`ph16-edit-${row.workflow_item_id}`}
      className="bg-amber-50/40 border-t border-amber-100 px-4 py-3 grid grid-cols-1 lg:grid-cols-3 gap-3"
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
          Workflow status
        </div>
        <select
          data-testid={`ph16-edit-status-${row.workflow_item_id}`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={`text-xs w-full border border-slate-200 rounded-md px-2 py-1.5 ${
            STATUS_TONES[status] || ""
          }`}
        >
          {statuses.map((s) => (
            <option key={s} value={s}>{prettyStatus(s)}</option>
          ))}
        </select>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
          Internal owner
        </div>
        <input
          data-testid={`ph16-edit-owner-${row.workflow_item_id}`}
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="ops-anna, ops-bob, etc."
          maxLength={120}
          className="text-xs w-full border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
          Internal note (append)
        </div>
        <textarea
          data-testid={`ph16-edit-note-${row.workflow_item_id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Manual follow-up notes — no emails are sent."
          rows={2}
          maxLength={1000}
          className="text-xs w-full border border-slate-200 rounded-md px-2 py-1.5 bg-white resize-y"
        />
      </div>
      <div className="lg:col-span-3 flex items-center gap-2 justify-end">
        <button
          onClick={onCancel}
          data-testid={`ph16-edit-cancel-${row.workflow_item_id}`}
          className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-full border border-slate-200"
        >
          Cancel
        </button>
        <button
          onClick={() =>
            onSave({
              workflow_status: status !== row.workflow_status ? status : null,
              internal_owner:
                owner !== (row.internal_owner || "") ? owner : null,
              internal_note: note.trim() ? note.trim() : null,
            })
          }
          disabled={!dirty || busy}
          data-testid={`ph16-edit-save-${row.workflow_item_id}`}
          className="text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60 px-3 py-1.5 rounded-full"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}


// ── Audit log preview ───────────────────────────────────────────────────
function AuditLog({ entries, notes }) {
  if (!entries?.length && !notes?.length) {
    return (
      <div className="text-[10px] italic text-slate-400">
        No history yet.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
          <History className="w-3 h-3" /> Audit log
        </div>
        <ul className="space-y-1 text-[11px]">
          {(entries || []).map((e, i) => (
            <li
              key={i}
              data-testid={`ph16-audit-entry-${i}`}
              className="rounded bg-slate-50 px-2 py-1"
            >
              <span className="font-mono text-[10px] text-slate-500">
                {fmtDateTime(e.changed_at)}
              </span>
              {" — "}
              {e.from_status !== e.to_status && e.to_status && (
                <span className="font-semibold">
                  {prettyStatus(e.from_status)} → {prettyStatus(e.to_status)}
                </span>
              )}
              {e.to_owner && (
                <span className="ml-2">owner: <strong>{e.to_owner}</strong></span>
              )}
              {e.actor_email && (
                <span className="ml-2 text-slate-500">by {e.actor_email}</span>
              )}
              {e.note && (
                <div className="italic text-slate-600">“{e.note}”</div>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
          <MessageSquare className="w-3 h-3" /> Internal notes
        </div>
        <ul className="space-y-1 text-[11px]">
          {(notes || []).map((n, i) => (
            <li
              key={i}
              data-testid={`ph16-note-${i}`}
              className="rounded bg-amber-50 border border-amber-100 px-2 py-1"
            >
              <span className="font-mono text-[10px] text-slate-500">
                {fmtDateTime(n.at)}
              </span>
              {n.by && (
                <span className="ml-1 text-slate-500">· {n.by}</span>
              )}
              <div className="text-slate-700">{n.note}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ── Workflow queue ──────────────────────────────────────────────────────
function WorkflowQueue({ data, statuses, busyId, expandedId, onToggle, onSave }) {
  const rows = data?.rows || [];
  return (
    <section
      data-testid="ph16-queue"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Workflow className="w-4 h-4 text-amber-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Partner Opportunity Workflow Queue
          </h3>
        </div>
        <div className="text-xs text-slate-500">
          {data?.showing ?? rows.length} / {data?.count ?? rows.length}
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="ph16-queue-empty"
          className="text-xs text-slate-400 italic py-6 text-center"
        >
          No partner opportunities match these filters.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph16-queue-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="w-6 p-2"></th>
                <th className="text-left p-2">Booking</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Partner</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">Est. value</th>
                <th className="text-left p-2">Workflow status</th>
                <th className="text-left p-2">Owner</th>
                <th className="text-left p-2">Last action</th>
                <th className="text-right p-2">Links</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = expandedId === r.workflow_item_id;
                return (
                  <React.Fragment key={r.workflow_item_id}>
                    <tr
                      data-testid={`ph16-row-${r.workflow_item_id}`}
                      className="border-t border-slate-100 align-top hover:bg-amber-50/30"
                    >
                      <td className="p-2">
                        <button
                          onClick={() => onToggle(r.workflow_item_id)}
                          data-testid={`ph16-row-toggle-${r.workflow_item_id}`}
                          className="text-slate-400 hover:text-amber-700"
                          aria-label="Expand row"
                        >
                          {isOpen ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </td>
                      <td className="p-2">
                        <Link
                          to={`/admin/bookings/${r.booking_reference}`}
                          data-testid={`ph16-row-booking-link-${r.workflow_item_id}`}
                          className="text-amber-700 hover:underline font-mono"
                        >
                          {r.booking_reference}
                        </Link>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {fmtDate(r.published_at)}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="text-slate-800">
                          {r.customer_name || "—"}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {r.customer_suburb || ""} {r.customer_postcode || ""}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="font-semibold text-slate-800">
                          {r.partner_business_name || (
                            <span className="text-slate-400 italic">(unknown)</span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          {r.partner_slug || r.partner_id}
                        </div>
                        {r.partner_franchise_region && (
                          <div className="text-[10px] text-slate-500">
                            {r.partner_franchise_region}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="font-semibold text-slate-800">
                          {r.category?.label || r.category_slug}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {r.main_finding}
                        </div>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        <div className="font-semibold text-emerald-800">
                          {fmtMoney(r.estimate_midpoint)}
                        </div>
                        <div className="text-[9px] text-slate-400 italic">
                          indicative
                        </div>
                      </td>
                      <td className="p-2">
                        <span
                          data-testid={`ph16-row-status-${r.workflow_item_id}`}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            STATUS_TONES[r.workflow_status] || ""
                          }`}
                        >
                          {prettyStatus(r.workflow_status)}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-1">
                          LV: {prettyStatus(r.lead_value_status)} · FU:{" "}
                          {prettyStatus(r.follow_up_status)}
                        </div>
                      </td>
                      <td className="p-2">
                        <div
                          className="flex items-center gap-1 text-slate-700"
                          data-testid={`ph16-row-owner-${r.workflow_item_id}`}
                        >
                          {r.internal_owner ? (
                            <>
                              <User className="w-3 h-3 text-amber-700" />
                              {r.internal_owner}
                            </>
                          ) : (
                            <span className="text-slate-400 italic">
                              unassigned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-[11px]">
                        <div>{fmtDate(r.last_action_at)}</div>
                        {r.last_action_by && (
                          <div className="text-[10px] text-slate-500">
                            {r.last_action_by}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        {r.passport_test_id && (
                          <Link
                            to={`/admin/lab/tests/${r.passport_test_id}`}
                            data-testid={`ph16-row-passport-link-${r.workflow_item_id}`}
                            className="text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1"
                          >
                            Water Record <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                        <div>
                          <Link
                            to="/admin/partners/follow-ups"
                            data-testid={`ph16-row-followup-link-${r.workflow_item_id}`}
                            className="text-[11px] text-violet-700 hover:underline inline-flex items-center gap-1"
                          >
                            Follow-Up <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        data-testid={`ph16-row-expanded-${r.workflow_item_id}`}
                        className="bg-amber-50/20"
                      >
                        <td colSpan={10} className="p-0">
                          <InlineEdit
                            row={r}
                            busy={busyId === r.workflow_item_id}
                            statuses={statuses}
                            onSave={(payload) => onSave(r, payload)}
                            onCancel={() => onToggle(r.workflow_item_id)}
                          />
                          <div className="px-4 py-3 border-t border-amber-100">
                            <AuditLog
                              entries={r.audit_log}
                              notes={r.internal_notes}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p
        data-testid="ph16-queue-disclaimer"
        className="mt-3 text-[10px] text-slate-400 italic"
      >
        {data?.disclaimer ||
          "Partner opportunity workflow is a manual admin tracking tool. No emails or work orders are sent from this page."}
      </p>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
import React from "react";

export default function AdminPWSOpportunityWorkflow() {
  const [windowSel, setWindowSel] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState({
    workflow_statuses: [], categories: [],
  });
  const [allRows, setAllRows] = useState({ rows: [], count: 0, showing: 0 });
  const [partnerId, setPartnerId] = useState(null);
  const [region, setRegion] = useState(null);
  const [category, setCategory] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [internalOwner, setInternalOwner] = useState(null);
  const [includeTerminal, setIncludeTerminal] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, items] = await Promise.all([
        api.get("/admin/pws-opportunity-workflow/summary",
                { params: { window: windowSel } }),
        api.get("/admin/pws-opportunity-workflow/catalog"),
        api.get("/admin/pws-opportunity-workflow/items", {
          params: {
            window: windowSel,
            ...(partnerId ? { partner_id: partnerId } : {}),
            ...(category ? { category } : {}),
            ...(workflowStatus ? { workflow_status: workflowStatus } : {}),
            ...(internalOwner ? { internal_owner: internalOwner } : {}),
            ...(region ? { region } : {}),
            include_terminal: includeTerminal,
            limit: 500,
          },
        }),
      ]);
      setSummary(s.data);
      setCatalog(c.data);
      setAllRows(items.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [windowSel, partnerId, category, workflowStatus, internalOwner,
      region, includeTerminal]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadData();
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [loadData]);

  const partners = useMemo(() => {
    const map = new Map();
    (allRows.rows || []).forEach((r) => {
      if (r.partner_id && !map.has(r.partner_id)) {
        map.set(r.partner_id, {
          id: r.partner_id,
          label: r.partner_business_name || r.partner_slug || r.partner_id,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  const regions = useMemo(() => {
    const set = new Set();
    (allRows.rows || []).forEach((r) => {
      if (r.partner_franchise_region) set.add(r.partner_franchise_region);
    });
    return [...set].sort();
  }, [allRows]);

  const owners = useMemo(() => {
    const set = new Set();
    (allRows.rows || []).forEach((r) => {
      if (r.internal_owner) set.add(r.internal_owner);
    });
    Object.keys(summary?.by_owner || {}).forEach((o) => {
      if (o !== "__unassigned__") set.add(o);
    });
    return [...set].sort();
  }, [allRows, summary]);

  const handleSave = async (row, payload) => {
    setBusyId(row.workflow_item_id);
    try {
      const { data } = await api.patch(
        `/admin/pws-opportunity-workflow/items/${encodeURIComponent(
          row.booking_reference,
        )}/${encodeURIComponent(row.category_slug)}`,
        payload,
      );
      const updated = data?.row;
      setAllRows((cur) => ({
        ...cur,
        rows: cur.rows.map((r) =>
          r.workflow_item_id === row.workflow_item_id
            ? {
                ...r,
                workflow_status: updated?.workflow_status ?? r.workflow_status,
                internal_owner: updated?.internal_owner ?? r.internal_owner,
                internal_notes: updated?.internal_notes ?? r.internal_notes,
                audit_log: updated?.audit_log ?? r.audit_log,
                last_action_at: updated?.last_action_at ?? r.last_action_at,
                last_action_by: updated?.last_action_by ?? r.last_action_by,
              }
            : r,
        ),
      }));
      toast.success("Workflow updated");
      setExpandedId(null);
    } catch (e) {
      toast.error(formatApiError(e) || "Workflow update failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleClearFilters = () => {
    setPartnerId(null);
    setRegion(null);
    setCategory(null);
    setWorkflowStatus(null);
    setInternalOwner(null);
    setIncludeTerminal(false);
  };

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-opportunity-workflow-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-800"
          data-testid="ph16-back-to-network"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-700">
              Aware Partner Network · Phase 16
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Opportunity Workflow
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Track each potential service opportunity from result →
              follow-up → manual partner / customer contact → quoted /
              won / lost / closed. All transitions are admin-only and
              audit logged.{" "}
              <strong className="text-slate-700">
                No automated emails, SMS, work orders or quotes are
                dispatched from this page.
              </strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <WindowSelector window={windowSel} onChange={setWindowSel} />
            <button
              onClick={loadData}
              disabled={loading}
              data-testid="ph16-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph16-safety-banner"
          className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3 text-sm text-amber-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Manual workflow only:</strong> updates here change
            <em> internal admin state only</em>. No outbound emails, SMS,
            ServiceM8 work orders, quotes or partner/customer
            notifications are sent. BioCleanse is{" "}
            <em>supplementary only</em>. Estimated values are indicative
            bands — not quotes. Use calm wording — &ldquo;potential
            service opportunity&rdquo;, &ldquo;recommended review&rdquo;,
            &ldquo;manual follow-up&rdquo;. Avoid medical claims.
          </div>
        </div>

        <StatStrip summary={summary} />

        <FilterBar
          partners={partners}
          regions={regions}
          categories={catalog.categories || []}
          statuses={catalog.workflow_statuses || []}
          owners={owners}
          partnerId={partnerId}
          region={region}
          category={category}
          workflowStatus={workflowStatus}
          internalOwner={internalOwner}
          includeTerminal={includeTerminal}
          onPartnerId={setPartnerId}
          onRegion={setRegion}
          onCategory={setCategory}
          onStatus={setWorkflowStatus}
          onOwner={setInternalOwner}
          onIncludeTerminal={setIncludeTerminal}
          onClear={handleClearFilters}
        />

        {loading ? (
          <div
            data-testid="ph16-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading workflow…
          </div>
        ) : error ? (
          <div
            data-testid="ph16-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <WorkflowQueue
            data={allRows}
            statuses={catalog.workflow_statuses || []}
            busyId={busyId}
            expandedId={expandedId}
            onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
            onSave={handleSave}
          />
        )}
      </div>
    </PortalShell>
  );
}
